import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import useCopilot from '../../hooks/useCopilot';
import useSession from '../../hooks/useSession';
import { IImage } from '../../global/interfaces';

interface AnalyzeDialogProps {
  open: boolean;
  onClose: () => void;
  analyzeSelectedImage: IImage | null;
  booking: any;
}

const AnalyzeDialog: React.FC<AnalyzeDialogProps> = ({
  open,
  onClose,
  analyzeSelectedImage,
  booking,
}) => {
  const { updateImage } = useSession();
  const { getImageAsBase64, modelSelect, modelList, analyzeImage } =
    useCopilot();
  const [promptText, setPromptText] = useState('');
  const [analyzeResult, setAnalyzeResult] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleAnalyze = async () => {
    if (!analyzeSelectedImage) return;

    setLoading(true);
    try {
      const response = await analyzeImage({
        image: await getImageAsBase64(`${analyzeSelectedImage.src}`),
        query: promptText,
        model: modelList[modelSelect],
      });

      setAnalyzeResult(response);
    } catch (error) {
      console.error('Error during analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setAnalyzeResult(null);
  };

  const handleSave = async () => {
    console.log('Saving result:', analyzeResult);
    if (!analyzeSelectedImage || !analyzeResult) return;

    if (!analyzeSelectedImage.description)
      analyzeSelectedImage.description = [];

    analyzeSelectedImage.description.push({
      query: promptText,
      model: modelList[modelSelect],
      response: analyzeResult,
    });

    await updateImage({
      bookingId: booking.booking_id,
      image: {
        id: analyzeSelectedImage.id,
        description: analyzeSelectedImage.description,
      },
    });

    setAnalyzeResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Enter Prompt</DialogTitle>
      <DialogContent
        sx={{
          minWidth: 'fit-content',
          width: '35rem',
        }}>
        <TextField
          autoFocus
          margin="dense"
          label="Prompt"
          type="text"
          fullWidth
          value={promptText}
          onChange={(e) => setPromptText(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />
        {loading ? (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress />
          </Box>
        ) : (
          analyzeResult && (
            <div style={{ marginTop: '1rem' }}>
              <Typography variant="body1">{analyzeResult}</Typography>
            </div>
          )
        )}
      </DialogContent>
      <DialogActions>
        {analyzeResult && (
          <>
            <Button onClick={handleDelete}>Dismiss</Button>
            <Button
              onClick={handleSave}
              color="primary"
              style={{ marginRight: '0.5rem' }}>
              Save
            </Button>
          </>
        )}
        {!analyzeResult && (
          <>
            <Button onClick={onClose} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleAnalyze}
              color="primary"
              disabled={!!analyzeResult || loading}>
              Analyze
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AnalyzeDialog;
